import { ColumnHeaderType, ColumnOrder } from '../../types/tables';

import { ProviderActivityType } from '../../types/activity';

import {
  ProviderActivityObject,
  getProviderActivityData,
} from '../../lib/providerActivity';
import {
  ActivityListDataFragment,
  ActivityType,
} from '../../../generated/graphql';
import {
  ProviderCollection,
  ProviderCollections,
} from '../../types/providerCollection';
import {
  isAssessmentAForm,
  isProviderOnlyAssessment,
} from '../../lib/assessments';
import { ContentType, activityTypeToContentType } from '../../lib/followUp';

export const sortOptions: Record<string, ColumnOrder> = {
  newestCreated: { name: 'newestCreated', isAscending: true },
  oldestCreated: { name: 'oldestCreated', isDescending: true },
  titleAlphabeticalAscending: {
    name: 'titleAlphabeticalAscending',
    isAscending: true,
  },
  titleAlphabeticalDescending: {
    name: 'titleAlphabeticalDescending',
    isDescending: true,
  },
};

export const PROVIDER_ACTIVITY_ORDERS: Record<string, ColumnOrder[]> = {
  activity: [
    sortOptions.titleAlphabeticalAscending,
    sortOptions.titleAlphabeticalDescending,
    sortOptions.newestCreated,
  ],
  dateAdded: [sortOptions.oldestCreated, sortOptions.newestCreated],
};

const columnHeaderOptions: Record<string, ColumnHeaderType> = {
  activity: {
    field: 'activity',
    headerName: 'Resource',
    isSortable: true,
    columnOrders: [
      sortOptions.titleAlphabeticalAscending,
      sortOptions.titleAlphabeticalDescending,
      sortOptions.newestCreated,
    ],
  },
  dateAdded: {
    field: 'dateAdded',
    headerName: 'Date added',
    isSortable: true,
    columnOrders: [sortOptions.oldestCreated, sortOptions.newestCreated],
  },
  activityActions: {
    field: 'activityActions',
    headerName: 'Actions',
    isSortable: false,
  },
};

export const columnHeaders: Record<string, ColumnHeaderType[]> = {
  activity: [
    columnHeaderOptions.activity,
    columnHeaderOptions.dateAdded,
    columnHeaderOptions.activityActions,
  ],
};

const getProviderActivityTypeData = (
  providerActivity: ActivityListDataFragment,
) => {
  const { createdAt, activityTitle } =
    getProviderActivityData(providerActivity);

  return {
    createdAt,
    activityTitle: activityTitle?.toLowerCase() ?? '',
  };
};

export const emptyFilterState = {
  [ActivityType.Link]: false,
  [ActivityType.Image]: false,
  [ActivityType.Pdf]: false,
  [ActivityType.Audio]: false,
  Form: false,
  [ActivityType.Assessment]: false,
  [ActivityType.Text]: false,
};

export const sortActivities = (
  activities: ActivityListDataFragment[] | undefined,
  activityOrder: ColumnOrder,
) => {
  if (activities) {
    return activities.sort(
      (
        activityA: ActivityListDataFragment,
        activityB: ActivityListDataFragment,
      ) => {
        const activityAData = getProviderActivityTypeData(activityA);
        const activityBData = getProviderActivityTypeData(activityB);

        switch (activityOrder) {
          case sortOptions.oldestCreated:
            return activityAData.createdAt < activityBData.createdAt ? -1 : 1;
          case sortOptions.newestCreated:
            return activityAData.createdAt >= activityBData.createdAt ? -1 : 1;

          case sortOptions.titleAlphabeticalAscending:
            return activityAData.activityTitle < activityBData.activityTitle
              ? -1
              : 1;
          case sortOptions.titleAlphabeticalDescending:
            return activityAData.activityTitle >= activityBData.activityTitle
              ? -1
              : 1;

          default:
            return activityAData.createdAt >= activityBData.createdAt ? -1 : 1;
        }
      },
    );
  }
  return [];
};

export const sortCollections = (
  collections: ProviderCollections | undefined,
  order: ColumnOrder,
) => {
  if (collections) {
    return collections.sort(
      (collectionA: ProviderCollection, collectionB: ProviderCollection) => {
        const nameA = collectionA.name.toLowerCase();
        const nameB = collectionB.name.toLowerCase();
        switch (order) {
          case sortOptions.oldestCreated:
            return collectionA.createdAt < collectionB.createdAt ? -1 : 1;
          case sortOptions.newestCreated:
            return collectionA.createdAt >= collectionB.createdAt ? -1 : 1;

          case sortOptions.titleAlphabeticalAscending:
            return nameA < nameB ? -1 : 1;
          case sortOptions.titleAlphabeticalDescending:
            return nameA >= nameB ? -1 : 1;

          default:
            return collectionA.createdAt >= collectionB.createdAt ? -1 : 1;
        }
      },
    );
  }
  return [];
};

export const getSelectedProviderActivitiesList = (
  selectedProviderActivitiesObject: ProviderActivityObject,
) => Object.values(selectedProviderActivitiesObject);

export const contentTypeFilterValues = (showActionField = false) => {
  return Object.values(ContentType)
    .map((val) => val as ContentType)
    .filter(
      (type) =>
        // PdfForm and Esign are excluded b/c they are filtered alongside PDF as "Files"
        // Assessment is excluded b/c assessments are in a separate section
        ![
          !showActionField && ContentType.Action,
          ContentType.PdfForm,
          ContentType.Esign,
          ContentType.Assessment,
        ].includes(type),
    );
};

interface Filters {
  selectedProviderCollection: ProviderCollection | undefined;
  searchTerm: string;
  contentTypeFilter?: ContentType | null;
  ignoreMultiSignatureForms?: boolean;
  includeCollectedInAllResources?: boolean; // If true, "All resources" shows resources in collections too
  excludeProviderOnlyForms?: boolean; // If true, exclude provider-only forms (e.g., in follow-up builder)
  includeOnlyPractitionerOnlyForms?: boolean; // If true, only show practitioner-only forms
}

export interface CollectionFilters extends Filters {
  allProviderActivities?: ActivityListDataFragment[];
  includeOnlyCollectionsWithPractitionerForms?: boolean;
}

export const doesActivityMeetFilters = (
  providerActivity: ActivityListDataFragment,
  filters: Filters,
): boolean => {
  let providerActivityTitle = '';
  let providerActivityURL = '';

  const {
    contentTypeFilter,
    searchTerm,
    selectedProviderCollection,
    ignoreMultiSignatureForms,
    includeCollectedInAllResources = false,
    excludeProviderOnlyForms = false,
    includeOnlyPractitionerOnlyForms = false,
  } = filters;

  // Collection view
  if (selectedProviderCollection) {
    // When viewing a collection, only show activities in that collection
    if (
      !providerActivity.providerCollectionIds.includes(
        selectedProviderCollection.id,
      )
    ) {
      return false;
    }
  } else {
    // When viewing "All resources", exclude activities that are in any collection
    // (unless includeCollectedInAllResources is true, e.g., for modals)
    if (
      !includeCollectedInAllResources &&
      providerActivity.providerCollectionIds.length > 0
    ) {
      return false;
    }
  }

  // Only practitioner-only forms: exclude everything else
  if (includeOnlyPractitionerOnlyForms) {
    if (
      providerActivity.__typename !== ProviderActivityType.Assessment ||
      !isProviderOnlyAssessment(providerActivity.assessment)
    ) {
      return false;
    }
  }

  // Search
  const isAssessment =
    providerActivity.__typename === ProviderActivityType.Assessment;

  if (isAssessment) {
    // Ignore multi-signature forms if specified
    if (
      ignoreMultiSignatureForms &&
      providerActivity.assessment?.assessmentSignatureUsers?.length > 0
    ) {
      return false;
    }

    // Exclude provider-only forms if specified (e.g., in follow-up builder where they can't be sent to patients)
    if (
      excludeProviderOnlyForms &&
      isProviderOnlyAssessment(providerActivity.assessment)
    ) {
      return false;
    }

    const isForm = isAssessmentAForm(providerActivity?.assessment);

    providerActivityTitle = isForm
      ? providerActivity?.assessment?.name.toLowerCase()
      : providerActivity?.assessment?.shortName.toLowerCase();
  } else if (
    providerActivity.__typename === ProviderActivityType.Image ||
    providerActivity.__typename === ProviderActivityType.PDF ||
    providerActivity.__typename === ProviderActivityType.Link ||
    providerActivity.__typename === ProviderActivityType.Audio ||
    providerActivity.__typename === ProviderActivityType.Text
  ) {
    providerActivityTitle = providerActivity?.title.toLowerCase();
  }

  if (providerActivity.__typename === ProviderActivityType.Link)
    providerActivityURL = providerActivity?.url;

  if (
    !providerActivityTitle.includes(searchTerm) &&
    !providerActivityURL.includes(searchTerm)
  ) {
    return false;
  }

  // Type filter
  if (contentTypeFilter) {
    const providerActivityContentType = activityTypeToContentType(
      providerActivity.activityType,
      isAssessment ? providerActivity.assessment?.schemaType : undefined,
    );
    if (contentTypeFilter !== providerActivityContentType) {
      return false;
    }
  }

  return true;
};

export const doesCollectionMeetFilters = (
  providerCollection: ProviderCollection,
  filters: CollectionFilters,
): boolean => {
  const {
    searchTerm,
    contentTypeFilter,
    selectedProviderCollection,
    allProviderActivities,
    includeOnlyCollectionsWithPractitionerForms,
    ignoreMultiSignatureForms,
    excludeProviderOnlyForms,
    includeOnlyPractitionerOnlyForms,
  } = filters;

  // Nested collection browsing is not supported
  if (selectedProviderCollection) {
    return false;
  }

  const needsMatchingActivity =
    Boolean(contentTypeFilter) ||
    Boolean(includeOnlyCollectionsWithPractitionerForms) ||
    Boolean(includeOnlyPractitionerOnlyForms);

  // When filtered by content type / practitioner forms, only show folders that
  // contain at least one matching activity
  if (needsMatchingActivity) {
    if (!allProviderActivities) {
      return false;
    }

    const hasMatchingActivity = allProviderActivities.some(
      (activity) =>
        activity.providerCollectionIds?.includes(providerCollection.id) &&
        doesActivityMeetFilters(activity, {
          contentTypeFilter,
          searchTerm: '',
          selectedProviderCollection: undefined,
          ignoreMultiSignatureForms,
          excludeProviderOnlyForms,
          includeOnlyPractitionerOnlyForms:
            includeOnlyPractitionerOnlyForms ||
            includeOnlyCollectionsWithPractitionerForms,
          includeCollectedInAllResources: true,
        }),
    );

    if (!hasMatchingActivity) {
      return false;
    }
  }

  if (searchTerm) {
    return providerCollection.name
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
  }

  return true;
};
